import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export type BadgeStatus = 'synced' | 'pending' | 'failed' | 'unknown' | 'active' | 'inactive';

export interface TrendData {
  direction: 'up' | 'down' | 'neutral';
  value: number;
  label?: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';
  subtitle?: string;
  link?: string;
  /** Secondary value displayed next to main value (e.g., "/15" for "12/15") */
  secondaryValue?: string | number;
  /** Trend indicator with direction and percentage */
  trend?: TrendData;
  /** Status badge displayed below value */
  badge?: {
    status: BadgeStatus;
    label?: string;
  };
  /** Loading state */
  loading?: boolean;
  /** Compact mode for smaller cards */
  compact?: boolean;
}

const badgeConfig: Record<BadgeStatus, { bg: string; text: string; defaultLabel: string }> = {
  synced: { bg: 'bg-success-100', text: 'text-success-700', defaultLabel: 'Synced' },
  pending: { bg: 'bg-warning-100', text: 'text-warning-700', defaultLabel: 'Pending' },
  failed: { bg: 'bg-danger-100', text: 'text-danger-700', defaultLabel: 'Failed' },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-600', defaultLabel: 'Unknown' },
  active: { bg: 'bg-success-100', text: 'text-success-700', defaultLabel: 'Active' },
  inactive: { bg: 'bg-gray-100', text: 'text-gray-600', defaultLabel: 'Inactive' },
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  color = 'primary',
  subtitle,
  link,
  secondaryValue,
  trend,
  badge,
  loading = false,
  compact = false,
}: StatCardProps) => {
  const colorClasses = {
    primary: 'bg-primary-900 text-white',
    success: 'bg-success-600 text-white',
    warning: 'bg-warning-600 text-white',
    danger: 'bg-danger-600 text-white',
    info: 'bg-info-600 text-white',
    secondary: 'bg-secondary-500 text-white',
  };

  const trendColors = {
    up: 'text-success-600',
    down: 'text-danger-600',
    neutral: 'text-gray-500',
  };

  const TrendIcon = trend?.direction === 'up' ? TrendingUp : trend?.direction === 'down' ? TrendingDown : Minus;

  if (loading) {
    return (
      <div className="card h-full animate-pulse">
        <div className="flex items-center justify-between h-full">
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
          <div className={`${compact ? 'w-10 h-10' : 'w-14 h-14'} bg-gray-200 rounded-lg`}></div>
        </div>
      </div>
    );
  }

  const content = (
    <div className="card h-full">
      <div className="flex items-center justify-between h-full">
        <div className="flex-1 min-w-0">
          <p className={`${compact ? 'text-xs' : 'text-sm'} font-heading font-medium text-gray-600 mb-1`}>{title}</p>
          <div className="flex items-baseline space-x-1 mb-1">
            <p className={`${compact ? 'text-2xl' : 'text-3xl'} font-title font-bold text-gray-900`}>{value}</p>
            {secondaryValue !== undefined && (
              <span className="text-lg font-semibold text-gray-400">{secondaryValue}</span>
            )}
          </div>

          {/* Trend indicator */}
          {trend && (
            <div className={`flex items-center space-x-1 ${trendColors[trend.direction]} text-xs`}>
              <TrendIcon className="w-3 h-3" />
              <span className="font-medium">{trend.value > 0 ? '+' : ''}{trend.value}%</span>
              {trend.label && <span className="text-gray-400">{trend.label}</span>}
            </div>
          )}

          {/* Badge */}
          {badge && (
            <div className="mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeConfig[badge.status].bg} ${badgeConfig[badge.status].text}`}>
                {badge.label || badgeConfig[badge.status].defaultLabel}
              </span>
            </div>
          )}

          {/* Subtitle (only shown if no trend or badge) */}
          {subtitle && !trend && !badge && (
            <p className="text-sm font-sans text-gray-500 truncate">{subtitle}</p>
          )}
        </div>
        <div className={`${compact ? 'w-10 h-10' : 'w-14 h-14'} ${colorClasses[color]} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={compact ? 'w-5 h-5' : 'w-7 h-7'} />
        </div>
      </div>
    </div>
  );

  return link ? (
    <Link to={link} className="block hover:opacity-90 transition-opacity">
      {content}
    </Link>
  ) : (
    content
  );
};

export default StatCard;
